/**
 * Trustpilot Widget Utility
 * Handles dynamic loading and initialization of Trustpilot widgets in React components
 */

class TrustpilotLoader {
    constructor() {
        this.isLoading = false;
        this.isLoaded = false;
        this.loadPromise = null;
    }

    /**
     * Load Trustpilot scripts if not already loaded
     * @returns {Promise} Promise that resolves when scripts are loaded
     */
    loadScripts() {
        if (this.isLoaded) {
            return Promise.resolve();
        }

        if (this.isLoading) {
            return this.loadPromise;
        }

        this.isLoading = true;
        this.loadPromise = new Promise((resolve, reject) => {
            // Check if scripts are already loaded
            if (window.Trustpilot) {
                this.isLoaded = true;
                this.isLoading = false;
                resolve();
                return;
            }

            let scriptsLoaded = 0;
            const totalScripts = 2;
            const errors = [];

            const onScriptLoad = () => {
                scriptsLoaded++;
                if (scriptsLoaded === totalScripts) {
                    this.isLoaded = true;
                    this.isLoading = false;
                    resolve();
                }
            };

            const onScriptError = (error) => {
                errors.push(error);
                scriptsLoaded++;
                if (scriptsLoaded === totalScripts) {
                    this.isLoading = false;
                    if (errors.length === totalScripts) {
                        reject(new Error('Failed to load Trustpilot scripts'));
                    } else {
                        // At least one script loaded successfully
                        this.isLoaded = true;
                        resolve();
                    }
                }
            };

            // Load bootstrap script
            const bootstrapScript = document.createElement('script');
            bootstrapScript.src = '//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
            bootstrapScript.async = true;
            bootstrapScript.onload = onScriptLoad;
            bootstrapScript.onerror = onScriptError;

            // Load sync bootstrap script
            const syncScript = document.createElement('script');
            syncScript.src = '//widget.trustpilot.com/bootstrap/v5/tp.widget.sync.bootstrap.min.js';
            syncScript.async = true;
            syncScript.onload = onScriptLoad;
            syncScript.onerror = onScriptError;

            document.head.appendChild(bootstrapScript);
            document.head.appendChild(syncScript);
        });

        return this.loadPromise;
    }

    /**
     * Initialize Trustpilot widgets in the given container
     * @param {HTMLElement} container - Container element to search for widgets
     */
    initializeWidgets(container = document) {
        if (!this.isLoaded || !window.Trustpilot) {
            console.warn('Trustpilot scripts not loaded yet');
            return;
        }

        try {
            const widgets = container.querySelectorAll('.trustpilot-widget');
            widgets.forEach(widget => {
                if (!widget.dataset.initialized) {
                    window.Trustpilot.loadFromElement(widget);
                    widget.dataset.initialized = 'true';
                }
            });
        } catch (error) {
            console.error('Error initializing Trustpilot widgets:', error);
        }
    }

    /**
     * Load scripts and initialize widgets
     * @param {HTMLElement} container - Container element to search for widgets
     * @returns {Promise} Promise that resolves when widgets are initialized
     */
    loadAndInitialize(container = document) {
        return this.loadScripts()
            .then(() => {
                // Small delay to ensure scripts are fully ready
                setTimeout(() => {
                    this.initializeWidgets(container);
                }, 100);
            })
            .catch(error => {
                console.error('Failed to load and initialize Trustpilot:', error);
            });
    }
}

// Create singleton instance
const trustpilotLoader = new TrustpilotLoader();

export default trustpilotLoader;
